import { useEffect, useState } from "react"
import { useParams } from "react-router"
import { Trash2, Plus } from "lucide-react"
import type { Family, Permission, RoleWithPermissions, User } from "@mc/types"
import {
  listServerRoles,
  createServerRole,
  updateServerRole,
  deleteServerRole,
  listServerMembers,
  setServerMemberRole,
  removeServerMember,
  listServerFamilies,
  addServerFamily,
  removeServerFamily,
  type ServerMemberEntry,
  type ServerFamilyEntry,
} from "../api/access.js"
import { listFamilies } from "../api/families.js"
import { friendlyError } from "../api/errorMessages.js"
import { Spinner } from "../components/ui/Spinner.js"
import { Button } from "../components/ui/Button.js"
import { useToast } from "../components/ui/Toast.js"
import { RoleBadge } from "../components/access/RoleBadge.js"
import { RoleEditor } from "../components/access/RoleEditor.js"
import { InviteMember } from "../components/access/InviteMember.js"
import { SwipeToDelete } from "../components/ui/SwipeToDelete.js"
import { Coachmark } from "../components/onboarding/Coachmark.js"
import { useFirstVisit } from "../components/onboarding/useFirstVisit.js"
import { getHint } from "../components/onboarding/hints.js"

type PageState =
  | { status: "loading" }
  | { status: "ready"; roles: RoleWithPermissions[]; members: ServerMemberEntry[]; families: ServerFamilyEntry[]; ownFamilies: Family[] }
  | { status: "error"; message: string }

/**
 * `/servers/[id]/access` (FR sections 3 and 8, "Acceso: roles del
 * servidor, miembros, familias con acceso"). Every mutation here is
 * gated server-side by Permission.ACCESS_MANAGE (services/gateway/src/
 * routes/access.ts) — a 403 surfaces as a normal friendlyError() toast,
 * this page doesn't pre-check the permission client-side (same
 * "tabs unfiltered for now" caveat ServerTabsLayout documents).
 */
export function ServerAccessPage() {
  const { serverId } = useParams<{ serverId: string }>()
  const [state, setState] = useState<PageState>({ status: "loading" })
  const [editingRole, setEditingRole] = useState<RoleWithPermissions | "new" | null>(null)
  const toast = useToast()
  const [isFirstVisit, dismissFirstVisit] = useFirstVisit("server-access-roles")

  useEffect(() => {
    if (serverId) load(serverId)
  }, [serverId])

  function load(id: string) {
    setState({ status: "loading" })
    Promise.all([listServerRoles(id), listServerMembers(id), listServerFamilies(id), listFamilies()])
      .then(([rolesRes, membersRes, familiesRes, ownFamiliesRes]) =>
        setState({
          status: "ready",
          roles: rolesRes.roles,
          members: membersRes.members,
          families: familiesRes.families,
          ownFamilies: ownFamiliesRes.families,
        }),
      )
      .catch((err) => setState({ status: "error", message: friendlyError(err).message }))
  }

  if (!serverId) return null

  if (state.status === "loading") {
    return (
      <div style={{ display: "grid", placeItems: "center", padding: "var(--space-12) 0" }}>
        <Spinner size="lg" />
      </div>
    )
  }

  if (state.status === "error") {
    return (
      <p role="alert" style={{ color: "var(--status-error)", fontSize: "var(--text-sm)" }}>
        {state.message}
      </p>
    )
  }

  const { roles, members, families, ownFamilies } = state

  async function handleSaveRole(input: { name: string; color: string; permissions: Permission[] }) {
    try {
      if (editingRole === "new") {
        await createServerRole(serverId!, input)
      } else if (editingRole) {
        await updateServerRole(serverId!, editingRole.id, input)
      }
      toast.show("Rol guardado.", "success")
      setEditingRole(null)
      load(serverId!)
    } catch (err) {
      toast.show(friendlyError(err).message, "error")
    }
  }

  function handleDeleteRole(role: RoleWithPermissions) {
    toast.confirmDestructive(
      `Se eliminará el rol "${role.name}". Deshacer disponible por 3 segundos.`,
      async () => {
        try {
          await deleteServerRole(serverId!, role.id)
          load(serverId!)
        } catch (err) {
          toast.show(friendlyError(err).message, "error")
        }
      },
    )
  }

  async function handleInviteMember(user: User) {
    try {
      await setServerMemberRole(serverId!, user.id, roles[0]?.id ?? null)
      toast.show(`${user.username} agregado.`, "success")
      load(serverId!)
    } catch (err) {
      toast.show(friendlyError(err).message, "error")
    }
  }

  async function handleSetMemberRole(userId: string, roleId: string | null) {
    try {
      await setServerMemberRole(serverId!, userId, roleId)
      load(serverId!)
    } catch (err) {
      toast.show(friendlyError(err).message, "error")
    }
  }

  function handleRemoveMember(member: ServerMemberEntry) {
    toast.confirmDestructive(
      `Se quitará a ${member.username} de este servidor. Deshacer disponible por 3 segundos.`,
      async () => {
        try {
          await removeServerMember(serverId!, member.userId)
          load(serverId!)
        } catch (err) {
          toast.show(friendlyError(err).message, "error")
        }
      },
    )
  }

  async function handleAddFamily(familyId: string) {
    try {
      await addServerFamily(serverId!, familyId, null)
      toast.show("Familia agregada.", "success")
      load(serverId!)
    } catch (err) {
      toast.show(friendlyError(err).message, "error")
    }
  }

  function handleRemoveFamily(entry: ServerFamilyEntry) {
    toast.confirmDestructive(
      `Se quitará el acceso de la familia "${entry.family.name}". Deshacer disponible por 3 segundos.`,
      async () => {
        try {
          await removeServerFamily(serverId!, entry.familyId)
          load(serverId!)
        } catch (err) {
          toast.show(friendlyError(err).message, "error")
        }
      },
    )
  }

  const rolesSection = (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ fontSize: "var(--text-2xl)" }}>Roles</h2>
        {editingRole === null && (
          <Button size="sm" onClick={() => setEditingRole("new")}>
            <Plus size={14} aria-hidden />
            Nuevo rol
          </Button>
        )}
      </div>

      {editingRole !== null && (
        <RoleEditor
          role={editingRole === "new" ? null : editingRole}
          onSave={handleSaveRole}
          onCancel={() => setEditingRole(null)}
        />
      )}

      {roles.length === 0 && editingRole === null && (
        <p style={{ color: "var(--text-muted)", fontSize: "var(--text-sm)" }}>Todavía no hay roles para este servidor.</p>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
        {roles.map((role) => (
          <SwipeToDelete key={role.id} onDelete={() => handleDeleteRole(role)}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "var(--space-3)",
                padding: "var(--space-3)",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--border-default)",
                background: "var(--bg-surface)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
                <RoleBadge role={role} />
                <span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
                  {role.permissions.length} permiso{role.permissions.length === 1 ? "" : "s"}
                </span>
              </div>
              <div style={{ display: "flex", gap: "var(--space-2)" }}>
                <Button size="sm" variant="secondary" onClick={() => setEditingRole(role)}>
                  Editar
                </Button>
                <Button size="sm" variant="destructive" aria-label={`Borrar rol ${role.name}`} onClick={() => handleDeleteRole(role)}>
                  <Trash2 size={14} aria-hidden />
                </Button>
              </div>
            </div>
          </SwipeToDelete>
        ))}
      </div>
    </section>
  )

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-8)" }}>
      <div>
        <h1 style={{ fontSize: "var(--text-3xl)" }}>Acceso</h1>
        <p style={{ color: "var(--text-muted)" }}>Roles, miembros y familias con acceso a este servidor.</p>
      </div>

      {isFirstVisit ? (
        <Coachmark title={getHint("server-access-roles").title} description={getHint("server-access-roles").description} onDismiss={dismissFirstVisit}>
          {rolesSection}
        </Coachmark>
      ) : (
        rolesSection
      )}

      <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <h2 style={{ fontSize: "var(--text-2xl)" }}>Miembros</h2>
        <InviteMember onInvite={handleInviteMember} actionLabel="Agregar miembro" />

        {members.length === 0 ? (
          <p style={{ color: "var(--text-muted)", fontSize: "var(--text-sm)" }}>Todavía no hay miembros individuales.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
            {members.map((member) => (
              <SwipeToDelete key={member.userId} onDelete={() => handleRemoveMember(member)}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "var(--space-3)",
                    padding: "var(--space-3)",
                    borderRadius: "var(--radius-md)",
                    border: "1px solid var(--border-default)",
                    background: "var(--bg-surface)",
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontSize: "var(--text-sm)" }}>{member.displayName ?? member.username}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                    <select
                      value={member.roleId ?? ""}
                      onChange={(e) => handleSetMemberRole(member.userId, e.target.value || null)}
                      style={{
                        padding: "6px 8px",
                        fontSize: "var(--text-sm)",
                        borderRadius: "var(--radius-sm)",
                        border: "1px solid var(--border-default)",
                        background: "var(--bg-surface)",
                        color: "var(--text-primary)",
                      }}
                    >
                      <option value="">Sin rol</option>
                      {roles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                    <Button
                      size="sm"
                      variant="destructive"
                      aria-label={`Quitar a ${member.displayName ?? member.username}`}
                      onClick={() => handleRemoveMember(member)}
                    >
                      <Trash2 size={14} aria-hidden />
                    </Button>
                  </div>
                </div>
              </SwipeToDelete>
            ))}
          </div>
        )}
      </section>

      <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <h2 style={{ fontSize: "var(--text-2xl)" }}>Familias con acceso</h2>
        {ownFamilies.length > 0 && (
          <div style={{ display: "flex", alignItems: "flex-end", gap: "var(--space-2)", flexWrap: "wrap" }}>
            <select
              id="add-family-select"
              defaultValue=""
              onChange={(e) => {
                if (e.target.value) {
                  handleAddFamily(e.target.value)
                  e.target.value = ""
                }
              }}
              style={{
                padding: "10px 12px",
                fontSize: "var(--text-sm)",
                borderRadius: "var(--radius-sm)",
                border: "1px solid var(--border-default)",
                background: "var(--bg-surface)",
                color: "var(--text-primary)",
              }}
            >
              <option value="">Agregar una familia…</option>
              {ownFamilies
                .filter((f) => !families.some((sf) => sf.familyId === f.id))
                .map((family) => (
                  <option key={family.id} value={family.id}>
                    {family.name}
                  </option>
                ))}
            </select>
          </div>
        )}

        {families.length === 0 ? (
          <p style={{ color: "var(--text-muted)", fontSize: "var(--text-sm)" }}>Ninguna familia tiene acceso todavía.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
            {families.map((entry) => (
              <SwipeToDelete key={entry.familyId} onDelete={() => handleRemoveFamily(entry)}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "var(--space-3)",
                    padding: "var(--space-3)",
                    borderRadius: "var(--radius-md)",
                    border: "1px solid var(--border-default)",
                    background: "var(--bg-surface)",
                  }}
                >
                  <span style={{ fontSize: "var(--text-sm)" }}>{entry.family.name}</span>
                  <Button
                    size="sm"
                    variant="destructive"
                    aria-label={`Quitar acceso de la familia ${entry.family.name}`}
                    onClick={() => handleRemoveFamily(entry)}
                  >
                    <Trash2 size={14} aria-hidden />
                  </Button>
                </div>
              </SwipeToDelete>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
